import { Corrections } from "./ChatTab";
import { Chip } from "./ui";
import { agreement, BTN, pct, TONE } from "./lib";

const COLORS = { ok: "#34d399", bad: "#f43f5e", warn: "#fbbf24", info: "#38bdf8", dim: "#1e5a8f" };

function Bar({ parts }) {
  const total = parts.reduce((s, [n]) => s + n, 0);
  return (
    <div className="mt-2.5 flex h-1.5 overflow-hidden rounded bg-[#0b2a50]">
      {total > 0 &&
        parts.map(([n, tone], i) => (
          <span key={i} style={{ width: `${(n / total) * 100}%`, background: COLORS[tone] }} />
        ))}
    </div>
  );
}

function Module({ title, value, sub, parts, facts, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="rounded-xl border border-sky-800/40 bg-[#041228]/70 p-3.5 text-left transition-colors hover:border-sky-500/60"
    >
      <div className="text-sm font-semibold text-white">{title}</div>
      <div className={`mt-1.5 text-3xl font-bold ${value === "—" ? "text-sky-400/50" : "text-white"}`}>{value}</div>
      <div className="text-[11px] text-sky-400/60">{sub}</div>
      <Bar parts={parts} />
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-sky-400/60">
        {facts.map(([n, label, tone]) => (
          <span key={label}>
            <b className={`font-semibold ${tone || "text-slate-200"}`}>{n}</b> {label}
          </span>
        ))}
      </div>
    </button>
  );
}

function Pill({ ok, warn, children }) {
  const dot = ok ? "bg-emerald-400" : warn ? "bg-amber-400" : "bg-rose-500";
  return (
    <span className="flex items-center gap-1.5 rounded-full border border-sky-800/40 bg-[#041228] px-2.5 py-1 text-[11px] text-sky-200">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

const fmtPct = (v) => (v === null ? "—" : `${v}%`);

/** Hamma bo'lim bir qarashda: holat, asosiy raqam, e'tibor kerak bo'lgan yozuvlar. */
export default function Overview({ s, go }) {
  const { chat, verify, visual, forms, status, attention } = s;
  const v = agreement(verify);
  const fb = visual?.stats.feedback;
  const jobs = visual?.stats.jobs;
  const q = forms?.questionnaire && !forms.questionnaire.error ? forms.questionnaire : null;
  const ik = forms?.intake && !forms.intake.error ? forms.intake : null;
  const st = status?.turn_stats;
  const corrections = chat?.corrections ?? [];
  const unanswered = status?.quality?.top_unanswered ?? [];
  const side = corrections.length > 0 || unanswered.length > 0;

  return (
    <div className="space-y-5">
      {status && (
        <div className="flex flex-wrap gap-2">
          <Pill ok={status.online}>
            {status.backend} · {status.model}
          </Pill>
          <Pill ok={status.vectors?.ready}>
            {status.vectors?.backend} · {status.vectors?.count?.toLocaleString("ru-RU")} vektor
          </Pill>
          <Pill ok={!status.quality?.error}>Postgres</Pill>
          {st?.count > 0 && <Pill ok={!st.slow} warn>Sekin javob · {st.slow}</Pill>}
          <Pill ok={!status.errors?.length}>LLM xatosi · {status.errors?.length ?? 0}</Pill>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {chat && (
          <Module
            title="Suhbat"
            value={fmtPct(chat.accuracy)}
            sub={`aniqlik · ${chat.rated} baholangan`}
            parts={[
              [chat.by_verdict.togri ?? 0, "ok"],
              [chat.by_verdict.xato ?? 0, "bad"],
              [chat.by_verdict.noaniq ?? 0, "warn"],
            ]}
            facts={[
              [chat.turns, "savol"],
              [chat.unrated, "baholanmagan", chat.unrated ? "text-amber-300" : ""],
            ]}
            onOpen={() => go("suhbat")}
          />
        )}
        {verify && (
          <Module
            title="Juftlik tekshiruvi"
            value={fmtPct(v.pct)}
            sub={`tekshiruvchi bilan mos · ${v.compared} dan`}
            parts={[
              [verify.agree, "ok"],
              [verify.partial, "info"],
              [verify.disagree, "bad"],
              [verify.ai_unsure, "warn"],
            ]}
            facts={[
              [verify.goods, "tovar"],
              [verify.disagree, "mos emas", verify.disagree ? "text-rose-300" : ""],
            ]}
            onOpen={() => go("juftlik")}
          />
        )}
        {visual && (
          <Module
            title="Fotodan tasvir"
            value={fmtPct(pct(fb.identity_ok, fb.rated))}
            sub={`to'g'ri tanilgan · ${fb.rated} baholangan`}
            parts={[
              [fb.identity_ok, "ok"],
              [fb.identity_wrong, "bad"],
            ]}
            facts={[
              [jobs.done, "tayyor"],
              [`$${jobs.avg_cost.toFixed(3)}`, "/ tovar"],
            ]}
            onOpen={() => go("tasvir")}
          />
        )}
        {forms && (
          <Module
            title="Savolnoma"
            value={q ? fmtPct(pct(q.done, q.total)) : "—"}
            sub={q ? `yakunlandi · ${q.total} sessiya` : forms.questionnaire?.error ?? ""}
            parts={
              q
                ? [
                    [q.confirmed, "ok"],
                    [q.refined, "info"],
                    [q.mismatch + q.unclear, "bad"],
                    [q.total - q.done, "dim"],
                  ]
                : []
            }
            facts={[
              [q?.avg_questions ?? "—", "savol / sessiya"],
              [ik?.total ?? "—", "kiritish"],
            ]}
            onOpen={() => go("savolnoma")}
          />
        )}
      </div>

      <div className={`grid grid-cols-1 gap-5 ${side ? "lg:grid-cols-2" : ""}`}>
        <section className="min-w-0 space-y-2">
          <h2 className="text-sm font-semibold text-white">Diqqat talab</h2>
          {attention.length === 0 ? (
            <div className="rounded-xl border border-sky-800/40 bg-[#041228]/60 py-8 text-center text-xs text-sky-400/60">
              Hammasi ko'rib chiqilgan
            </div>
          ) : (
            <div className="divide-y divide-sky-900/40 rounded-xl border border-sky-800/40 bg-[#030f24]/60">
              {attention.map((a, i) => (
                <div key={i} className="flex items-center gap-2.5 px-3 py-2.5">
                  <Chip cls={TONE[a.tone]}>{a.label}</Chip>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs text-slate-200">{a.title}</div>
                    <div className="truncate text-[10px] text-sky-400/60">{a.sub}</div>
                  </div>
                  <button onClick={() => go(...a.go)} className={BTN}>
                    Ochish
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {side && (
          <div className="min-w-0 space-y-5">
            {corrections.length > 0 && <Corrections items={corrections} limit={6} />}
            {unanswered.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-white">Javobsiz savollar</h2>
                <div className="divide-y divide-sky-900/40 rounded-xl border border-sky-800/40 bg-[#030f24]/60 text-[11px]">
                  {unanswered.slice(0, 5).map((u, i) => (
                    <div key={i} className="flex gap-2 px-3 py-2">
                      <span className="font-mono text-amber-300">{u.count}×</span>
                      <span className="min-w-0 flex-1 truncate text-slate-200">{u.question}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
